import React, {Component} from 'react';
import axios from 'axios';
import ReactTable from 'react-table';
import {RESOURCES as reportsResources} from '../fuseReportsResourceMap';
import {REPORT_FREQUENCY, WEEKDAYS} from '../shared/dateTimeLists';
import Config from '../messages';
import {convertDate, convertTimeFromHoursMinutes, getTodayOffsetHour, sortArrayByKey} from '../shared/functions';
import {getGetHeaders} from '../shared/serverFunctions';
import {setDefaultTableSort, getTableState, filterCaseInsensitive} from '../shared/tableFunctions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import EditButtonByCustomKey from '../buttons/EditButtonByCustomKey';
import RenderReport from './RenderReport';
import ViewButtonByCustomKey from '../buttons/ViewButtonByCustomKey';
import CircularProgress from 'material-ui/CircularProgress';

/**
 * Scheduled Reports class for retrieving and displaying summary
 *
 */
export class ScheduledReportsTable extends Component {
    constructor(props) {
        super(props);

        const offsetHour = getTodayOffsetHour();

        this.state = {
            errorMessage: null,
            offsetHour: offsetHour,
            summaryComplete: false,
            summaryTableData: []
        };
    }

    componentWillMount() {
        this.fetchData();
    }

    fetchData() {

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        const params = {};
        let resource = reportsResources.ScheduledReports.GET_LIST(params);
        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processData(result.data.payload);
            }
        }).catch((error) => {
            let message = Config.fuse.error.error + 'Scheduled Reports';
            if (error.response) {
                // Server response outside 2xx
                if (error.response.data && error.response.data.error) {
                    message = Config.fuse.error.error + 'Scheduled Reports: ' + error.response.data.error;
                }
            } else if (error.request) {
                // No response
                console.log(error.request);
            } else {
                console.log('Error', error.message);
            }
            this.setState({
                errorMessage: message,
                summaryList: [],
                summaryTableData: [],
                summaryComplete: true
            });
        });
    }

    processData = (data) => {
        let summaryList = [];
        for (let i = 0; i < data.length; i++) {
            let report = data[i];
            if (report && report.name && report.frequency) {
                report.filename = report.name.replace(/ /g, '-').replace(/^[[A-Za-z0-9]-_]+$/);
                // Assemble Frequency strings
                let freq = REPORT_FREQUENCY.find(r => r.id === report.frequency);
                let day = (report.report_day !== '') ? WEEKDAYS.find(d => d.id === report.report_day) : null;
                report.frequency_str = (day) ? day.name + 's' : freq.name;
                report.run_time_str = convertTimeFromHoursMinutes(report.run_time, 0);
                report.modified_str = (report.modified) ? convertDate(report.modified, 'display', true, true, this.state.offsetHour, true) : 'No Date';
                // Assemble Date Range strings
                let dateRange = report.payload.dateRange;
                let daysBack = dateRange.daysBack;
                report.range_str = (daysBack > 0)
                    ? 'Runtime back ' + daysBack + ' days'
                    : dateRange.fromDay + ' to ' + dateRange.toDay;

                summaryList.push(report);
            }
        }
        const sorted = sortArrayByKey(summaryList, 'modified', 'DESC');

        // Finalize table data
        this.setState({
            errorMessage: null,
            summaryList: sorted,
            summaryTableData: sorted,
            summaryComplete: true,
        });
    };

    /**
     * Update selected table data based on sort
     *
     * @param event
     * @param table
     */
    handleSortOrFilterChange(event, table) {
        const tableData = getTableState(table);
        if (tableData && tableData.length > 0) {
            this.setState({
                summaryTableData: tableData
            });
        }
    }

    /**
     * Handle event props for Table completion
     *
     * @param isComplete
     */
    handleSummaryComplete = (isComplete) => {
        this.setState({
            summaryComplete: isComplete,
        });
    };

    render() {
        const {
            errorMessage,
            summaryComplete,
            summaryList
        } = this.state;

        const defaultSort = setDefaultTableSort('job_name');

        // ReactTable headers and columns for Table
        const tableColumns = [
            {
                Header: 'Name',
                accessor: 'name'
            },
            {
                Header: 'Frequency',
                accessor: 'frequency_str',
                Cell: row => (
                    <span>{row.original.frequency_str} at {row.original.run_time_str}</span>
                )
            },
            {
                Header: 'Range',
                accessor: 'range_str'
            },
            {
                Header: 'Date',
                accessor: 'modified',
                Cell: row => (
                    <span>{row.original.modified_str}</span>
                )
            },
            {
                Header: '',
                id: 'Scheduled-Report-View',
                Cell: row => (
                    <span>
                        <ViewButtonByCustomKey record={row.original} basePath="/ScheduledReports" customKey="uuid" />
                    </span>
                ),
                filterable: false,
                sortable: false,
                width: 60
            },
            {
                Header: '',
                Cell: row => (
                    <span>
                        <EditButtonByCustomKey record={row.original} basePath="/ScheduledReports" customKey="uuid" />
                    </span>
                ),
                id: 'Scheduled-Report-Edit',
                filterable: false,
                sortable: false,
                width: 60
            },
            {
                Header: '',
                Cell: row => (
                    <RenderReport
                        payload={row.original.payload}
                        label="Print"
                        buttonType="icon"
                        filename={`Scheduled-Report-${row.original.filename}`}
                    />
                ),
                id: 'Scheduled-Report-Print',
                filterable: false,
                sortable: false,
                width: 60
            }
        ];

        return (
            <div>

                {!summaryComplete && !errorMessage &&
                    <div className="loader-container">
                        <h4>Retrieving Scheduled Reports...</h4>
                        <CircularProgress
                            size={24}
                            thickness={2}
                            className="loader"
                        />
                    </div>
                }
                {errorMessage &&
                    <div className="error">
                        <p>{errorMessage}</p>
                    </div>
                }

                {summaryComplete && summaryList.length > 0 &&
                    <div className="reports-table-container">

                        {/* Table Header: Scheduled Reports */}
                        <div className="reports-table-title table-table-header">
                            <div className="btn-report-container report-header-button-container">
                            </div>
                            <h3 className="table-table-header-title">
                                <span>Scheduled Reports</span>
                            </h3>
                        </div>

                        {/* Table: Scheduled Reports */}
                        <ReactTable
                            key="table-scheduled-reports"
                            data={summaryList}
                            columns={tableColumns}
                            defaultPageSize={20}
                            showPagination={true}
                            defaultSorted={defaultSort}
                            filterable
                            defaultFilterMethod={filterCaseInsensitive}
                            onSortedChange={(e) => this.handleSortOrFilterChange(e, this.scheduledReactTable)}
                            onFilteredChange={(e) => this.handleSortOrFilterChange(e, this.scheduledReactTable)}
                            ref={(r) => this.scheduledReactTable = r}
                            resizable={false}
                            minRows={1}
                            className="-striped"
                        />
                    </div>
                }

            </div>
        )
    }
}



// WEBPACK FOOTER //
// ./src/Fuse/components/ScheduledReportsTable.js